import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PropertyType } from '../../../property/shared/models';
import { PropertySearchFilter } from '../../shared/models';
import { PropertySearchResult } from '../../shared/models/property-search-result';
import { Router } from '@angular/router';
import { PropertyFilterSettings } from '../../../shared/models/filter-settings';
import { trigger } from 'devextreme/events';
import { ModalComponent } from 'app/internal/shared';
import { AdminReassignPropertyModalComponent } from '../admin-reassign-property-modal/admin-reassign-property-modal.component';
import { UserProfile } from 'app/internal/user/models';

@Component({
  selector: 'sp-admin-property-list',
  templateUrl: './admin-property-list.component.html',
  styleUrls: ['./admin-property-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminPropertyListComponent implements OnInit, OnChanges {

  @Input() propertySearchResult: PropertySearchResult;
  @Input() propertyFilterSettings: PropertyFilterSettings;
  @Input() propertyTypes: PropertyType[];
  @Input() loggedInUserProfile: UserProfile;
  @Output() clearCache = new EventEmitter<string>();
  @Output() filterChanged = new EventEmitter<PropertySearchFilter>();
  @Output() deactivate = new EventEmitter<string>();
  @Output() reactivate = new EventEmitter<string>();
  @Output() duplicate = new EventEmitter<string>();
  @Output() reassign = new EventEmitter<{ propertyId: string, destinationWorkspaceId: string, includeUsers: boolean }>();

  @ViewChild('deactivatePropertyStatusModal', { static: true }) public deactivatePropertyStatusModal: ModalComponent;
  @ViewChild('reactivatePropertyStatusModal', { static: true }) public reactivatePropertyStatusModal: ModalComponent;
  @ViewChild('duplicatePropertyModal', { static: true }) public duplicatePropertyModal: ModalComponent;
  @ViewChild('reassignPropertyModal', { static: true }) public reassignPropertyModal: AdminReassignPropertyModalComponent;

  toggleStatusPropertyId: string;
  duplicatePropertyId: string;
  dataGridSource: any;
  dropDownValues = [
    { value:'active', text: 'Active' }, 
    { value: 'inactive', text: 'Inactive' }, 
    { value: 'all', text: 'All' }
  ];

  constructor(private router: Router) { }

  ngOnInit() {
    if (this.hasFilterData()) {
      this.onFilterChanged(this.propertyFilterSettings);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.propertySearchResult && this.propertySearchResult) {
      this.onActiveDropdownChanged('active');
    }
  }

  onActiveDropdownChanged(selectedValue: string) {
    if (selectedValue === 'active') {
      this.dataGridSource = this.propertySearchResult?.properties?.filter(x => x.isActive == true);
    } else if (selectedValue === 'inactive') {
      this.dataGridSource = this.propertySearchResult?.properties?.filter(x => x.isActive == false);
    } else {
      this.dataGridSource = this.propertySearchResult?.properties;
    }
  }

  hasFilterData(): boolean {
    return this.propertyFilterSettings && !this.isStringNullOrEmpty(this.propertyFilterSettings.filter);
  }

  hasFilterDataAndResults(): boolean {
    return this.hasFilterData() && this.propertySearchResult?.properties?.length > 0;
  }

  onFilterChanged(propertyFilterSettings: PropertyFilterSettings) {
    this.propertyFilterSettings = propertyFilterSettings;

    const filter = new PropertySearchFilter(propertyFilterSettings.filter, propertyFilterSettings.propertyTypeId);
    this.filterChanged.emit(filter);
  }

  dxCalculateCellValueForIsTestColumn(rowData: any) {
    return rowData.isTest ? 'Yes' : 'No';
  }

  dxCalculateCellValueForLocationColumn(rowData: any) {
    return `${rowData.city}, ${rowData.state}`;
  }

  dxGridRowPrepared(e) {
    // https://supportcenter.devexpress.com/ticket/details/t744353/datagrid-how-to-change-the-row-height    
    if (e.rowType === 'data') {
      e.rowElement.style.height = '60px';
    }
  }

  showActionMenu(e) {
    // This allows us to either left or right click to show the menu.
    const newEvent = Object.assign({}, e.event, { type: 'dxcontextmenu' });
    e.event.stopPropagation();
    trigger(e.element, newEvent);
  }

  dxOnContextMenuPreparing(e) {

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // Only show the Context Menu if the Menu Button is clicked!
    if (e.row.rowType === 'data' && e.column.cellTemplate === 'propertyId') {

      const propertyId = e.row.data.propertyId;
      const workspaceId = e.row.data.workspaceId;

      const toggleActiveStatusMenuItem = {
        text: e.row.data.isActive ? 'Deactivate' : 'Reactivate',
        onItemClick: function () {
          that.toggleStatusPropertyId = propertyId;
          if (e.row.data.isActive) {
            that.deactivatePropertyStatusModal.show();
          } else {
            that.reactivatePropertyStatusModal.show();
          }
        }
      };

      e.items = [
        {
          text: 'Edit',
          onItemClick: function () {
            if (workspaceId) {
              if (that.loggedInUserProfile?.isSmartPropertyAdmin) {
                that.router.navigate(['/useraccount/admin/portfolios/' + workspaceId + '/edit-property/' + propertyId], { queryParams: { navigatedFromPropertyList: 'true' } });
              } else {
                that.router.navigate(['/useraccount/admin/properties/' + propertyId]);
              }
            } else {
              alert('This Property isnt linked to a Workspace?!?');
            }
          }
        },
        {
          text: 'Clear Cache',
          onItemClick: function () {
            that.clearCache.emit(propertyId);
          }
        },
      ];

      if (this.loggedInUserProfile?.isSmartPropertyAdmin) {
        e.items.push(
          {
            text: 'Reassign',
            onItemClick: function () {
              if (workspaceId) {
                that.reassignPropertyModal.show(propertyId, e.row.data.name, e.row.data.workspaceId);
              } else {
                alert('This Property isnt linked to a Workspace?!?');
              }
            }
          });

        e.items.push(toggleActiveStatusMenuItem);

        e.items.push(
          {
            text: 'Duplicate',
            onItemClick: function () {
              that.duplicatePropertyId = propertyId;
              that.duplicatePropertyModal.show();
            }
          });

        e.items.push(
          {
            text: 'Export Map',
            onItemClick: function () {
              that.router.navigate([`/useraccount/admin/properties/${propertyId}/export-map`]);
            }
          });
      }
    }
  }

  deactivateProperty() {
    this.deactivate.emit(this.toggleStatusPropertyId);
    this.toggleStatusPropertyId = null;
  }

  reactivateProperty() {
    this.reactivate.emit(this.toggleStatusPropertyId);
    this.toggleStatusPropertyId = null;
  }

  onReassignPropertyConfirm(data: any) {
    this.reassign.emit({ propertyId: data.propertyId, destinationWorkspaceId: data.destinationWorkspaceId, includeUsers: data.includeUsers });
  }

  duplicateProperty() {
    this.duplicate.emit(this.duplicatePropertyId);
    this.duplicatePropertyId = null;
  }

  private isStringNullOrEmpty(value: string) {
    return value === null || value === '';
  }
}
